import {
    Badge,
    Box,
    Button,
    HStack,
    IconButton,
    Spinner,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useClipboard,
    useColorModeValue,
    VStack,
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { FaCopy } from 'react-icons/fa';
import PageCard from '../../../components/page-card';
import { translate, TranslationKeys } from '../../../i18n';
import {
    AccessCredentialStatus,
    AdminAccessCredentialDto,
} from '../../../models/access-control/dto/booking-access-credential.dto';
import { useClubhouseTypography } from '../../../theme/tokens';
import { classifyRead } from '../../_shared/readState';
import { getProviderLabel } from '../../access-control/utils/access-control.utils';
import { useGetGameAccessCredentials } from '../hooks/useGetGameAccessCredentials';

interface Props {
    organizationId?: string;
    gameId: number;
}

/**
 * Same rendering as `GameDetailsPage`'s `formatDate`, done locally on purpose:
 * importing it here would make a leaf card depend on the whole page module
 * (and back, since the page renders this card).
 */
const formatDateTime = (value: string) => moment(value).format('DD MMM YYYY - HH:mm');

const STATUS_COLOR_SCHEME: Record<AccessCredentialStatus, string> = {
    [AccessCredentialStatus.ACTIVE]: 'green',
    [AccessCredentialStatus.USED]: 'blue',
    [AccessCredentialStatus.REVOKED]: 'red',
    [AccessCredentialStatus.EXPIRED]: 'gray',
};

/** ACTIVE is the only status that can still open a door. */
function isUsable(credential: AdminAccessCredentialDto) {
    return credential.status === AccessCredentialStatus.ACTIVE;
}

const CodeCell: React.FC<{ credential: AdminAccessCredentialDto }> = ({ credential }) => {
    const { dataValue } = useClubhouseTypography();
    const { onCopy, hasCopied } = useClipboard(credential.credentialValue);
    const mutedColor = useColorModeValue('gray.500', 'gray.400');

    // Blanked by the backend's retention sweep — showing an empty cell would
    // read as "no code was issued", which is a different thing entirely.
    if (!credential.credentialValue) {
        return (
            <Text fontSize="sm" color={mutedColor} fontStyle="italic">
                {translate('access_control.code_no_longer_available')}
            </Text>
        );
    }

    return (
        <HStack spacing={2}>
            <Text {...dataValue} fontSize="md" fontWeight="600" letterSpacing="0.08em">
                {credential.credentialValue}
            </Text>
            <Tooltip
                label={hasCopied ? translate('access_control.code_copied') : translate('access_control.copy_code')}
                closeOnClick={false}
            >
                <IconButton
                    aria-label={translate('access_control.copy_code')}
                    icon={<FaCopy />}
                    size="xs"
                    variant="ghost"
                    onClick={onCopy}
                />
            </Tooltip>
        </HStack>
    );
};

/**
 * Operator view of the access-control codes (igloohome PINs and friends)
 * generated for a game — one row per participant, plus the revoked
 * predecessors a reissue left behind (a time or court change revokes every
 * code and issues new ones). Revoked/expired rows are collapsed by default so
 * the codes an operator can actually read out stay at the top.
 *
 * Owns its own `PageCard` rather than being wrapped by the page, because it
 * renders nothing at all when the org has no access-control integration: the
 * endpoint returns an empty list, and an empty "Access Codes" card on every
 * game detail page would be noise for the majority of tenants.
 */
const AccessCredentialsCard: React.FC<Props> = ({ organizationId, gameId }) => {
    const { data, isInitialLoading, isError } = useGetGameAccessCredentials(organizationId, gameId);
    const readState = classifyRead(isError, isInitialLoading, data);
    const [showHistory, setShowHistory] = useState(false);
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');

    const { usable, history } = useMemo(() => {
        const credentials = data?.credentials ?? [];
        return {
            usable: credentials.filter(isUsable),
            history: credentials.filter((c) => !isUsable(c)),
        };
    }, [data]);

    if (readState === 'loading') {
        return (
            <PageCard title={translate('access_control.game_codes_title')}>
                <Box display="flex" justifyContent="center" py={4}>
                    <Spinner size="sm" />
                </Box>
            </PageCard>
        );
    }

    /**
     * R114-2 — nobody asked, so this card says nothing, exactly as it does for
     * an org with no access-control integration.
     *
     * MEASURED NEGATIVE, recorded rather than claimed as a fix: this branch is
     * not reachable today. `GameDetailsPage` passes
     * `game.court?.location?.organizationId ?? selectedOrganization?.id`, and
     * `GET games/:id/details` populates `court.location.organization`
     * (`findGameFullyPopulatedOrFail` in `api/src/game/service/game.service.ts`),
     * so the id arrives with the game the card is rendered for; the second gate
     * term is `game.id`. The branch exists because the gate is the hook's, not
     * the caller's — the day a caller passes an id it does not have, the honest
     * render is silence and not a spinner that resolves for nobody.
     *
     * Silence rather than a `ScopePrompt`: this is a leaf card that already
     * self-hides, mounted inside someone else's page, and a prompt panel there
     * would be the `WeatherStrip` false positive the census recorded at run 110
     * — a sentence about the whole page, printed by a card that is not the page.
     */
    if (readState === 'never-issued') {
        return null;
    }

    // A 403 here means a coach account — the endpoint is org ADMIN-gated.
    //
    // R114-2 — keyed off `readState`, not off raw `isError`, and that is the
    // live half of this file's fix. `classifyRead`'s `unreadable` requires
    // `!data` (R96-3), so a BACKGROUND refetch that fails over codes already on
    // screen no longer replaces them with this message. It is not a rare path
    // here: the query sets `staleTime: 60s` and nothing overrides React Query's
    // default `refetchOnWindowFocus`, so an operator who reads a PIN out, tabs
    // away and comes back was one failed refetch away from losing the door
    // codes they were mid-way through reading.
    if (readState === 'unreadable') {
        return (
            <PageCard title={translate('access_control.game_codes_title')}>
                <Text color="red.400" fontSize="sm" px={6} py={2}>
                    {translate('access_control.error_loading_credentials')}
                </Text>
            </PageCard>
        );
    }

    if (usable.length === 0 && history.length === 0) {
        return null;
    }

    const rows = showHistory ? [...usable, ...history] : usable;

    return (
        <PageCard title={`${translate('access_control.game_codes_title')} (${usable.length})`}>
        <VStack align="stretch" spacing={3} px={6} pb={4}>
            {usable.length === 0 && (
                <Text color={mutedColor} fontSize="sm">
                    {translate('access_control.no_usable_credentials')}
                </Text>
            )}

            {rows.length > 0 && (
                <TableContainer>
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th borderColor={borderColor}>{translate('access_control.credential_player')}</Th>
                                <Th borderColor={borderColor}>{translate('access_control.credential_code')}</Th>
                                <Th borderColor={borderColor}>{translate('access_control.credential_status')}</Th>
                                <Th borderColor={borderColor}>{translate('access_control.credential_valid_from')}</Th>
                                <Th borderColor={borderColor}>{translate('access_control.credential_valid_until')}</Th>
                                <Th borderColor={borderColor}>{translate('access_control.door_id')}</Th>
                                <Th borderColor={borderColor}>{translate('access_control.provider')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {rows.map((credential) => (
                                <Tr key={credential.id} opacity={isUsable(credential) ? 1 : 0.6}>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize="sm">
                                            {credential.userName || translate('access_control.credential_unknown_player')}
                                        </Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <CodeCell credential={credential} />
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Badge colorScheme={STATUS_COLOR_SCHEME[credential.status] ?? 'gray'}>
                                            {translate(
                                                `access_control.credential_status_${credential.status}` as TranslationKeys,
                                            )}
                                        </Badge>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize="sm">{formatDateTime(credential.validFrom)}</Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize="sm">{formatDateTime(credential.validUntil)}</Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize="sm">{credential.doorIds.join(', ') || '-'}</Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize="sm">{getProviderLabel(credential.provider)}</Text>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}

            {history.length > 0 && (
                <Button
                    size="xs"
                    variant="link"
                    alignSelf="flex-start"
                    onClick={() => setShowHistory(!showHistory)}
                >
                    {showHistory
                        ? translate('access_control.hide_credential_history')
                        : translate('access_control.show_credential_history', { count: history.length })}
                </Button>
            )}
        </VStack>
        </PageCard>
    );
};

export default AccessCredentialsCard;
